import { noop } from '@packages/core/utils';
import { useCallback } from 'react';

import type { FailCodeSelection, FailCodeVehicleParams } from '../fail-codes-modal.types';

import { useLookupByElectricalGrid } from '../hooks/use-lookup-by-electrical-grid';
import { ElectricalGridStepOne } from './electrical-grid-step-one';
import { ElectricalGridStepTwo } from './electrical-grid-step-two/electrical-grid-step-two';

/**
 * Props for the {@link LookupByElectricalGridLocation} component.
 */
export type LookupByElectricalGridLocationProps = {
    /** Callback invoked when the user navigates back to the search method selector. */
    onBack: () => void;

    /** Callback invoked when the user closes the modal entirely. */
    onClose: () => void;

    /**
     * Callback invoked when the user confirms a fail code selection from the electrical grid.
     * Receives the finalized {@link FailCodeSelection} to persist on the techline case.
     */
    onConfirmFailCode: (selection: FailCodeSelection) => void;

    /** Vehicle params used to scope wiring diagram lookups, already mapped from VIN data by the parent. */
    vehicleParams: FailCodeVehicleParams;
};

/**
 * Two-step fail code lookup component that uses wiring diagram electrical grids.
 *
 * Step 1: Select a wiring diagram subsection from a typeahead dropdown.
 * Step 2: View the diagram image and click on a grid cell to select a fail code location.
 *
 * The component is presentational; all state and API interactions are managed by the
 * {@link useLookupByElectricalGrid} hook.
 *
 * @param props - {@link LookupByElectricalGridLocationProps}
 * @returns the electrical grid lookup JSX
 */
export function LookupByElectricalGridLocation({
    onBack,
    onClose,
    onConfirmFailCode,
    vehicleParams,
}: LookupByElectricalGridLocationProps) {
    // Hooks
    // Self-contained read-only exception: this hook performs only reads (wiring diagram lookups);
    // the parent receives the final FailCodeSelection via onConfirmFailCode. The full cascade
    // lifecycle (fetching diagrams, diagram images, grid clicks) is managed internally.
    const grid = useLookupByElectricalGrid(vehicleParams);

    // Forwarded callback aliases
    const handleBack = onBack ?? noop;

    // Handlers

    /**
     * Handle the user clicking "Add Fail Code" in step 2. Builds the fail code selection
     * from the grid state and, if valid, passes it to the parent and closes the modal.
     */
    const handleAddFailCodeButtonClick = useCallback(() => {
        const selection = grid.buildFailCodeSelection();

        if (!selection) {
            alert('No Fail Code has been selected.');
            return;
        }

        onConfirmFailCode(selection);
        onClose();
    }, [grid, onClose, onConfirmFailCode]);

    return (
        <>
            {grid.isOnSelectDiagramStep && (
                <ElectricalGridStepOne
                    diagramOptionData={grid.diagramOptionData}
                    hasDiagramSelected={grid.hasDiagramSelected}
                    onBack={handleBack}
                    onClose={onClose}
                    onContinue={grid.handleContinueButtonClick}
                    onDiagramChange={grid.handleDiagramTypeaheadChange}
                    selectedDiagramData={grid.selectedDiagramData}
                />
            )}
            {grid.isOnSelectGridLocationStep && (
                <ElectricalGridStepTwo
                    diagramImageUrl={grid.diagramImageUrl}
                    gridRange={grid.gridRange}
                    onAddFailCode={handleAddFailCodeButtonClick}
                    onBack={grid.handleBackToSelectDiagramClick}
                    onClose={onClose}
                    onGridFailCodeSelect={grid.handleGridFailCodeSelect}
                    onZoomIn={grid.handleZoomInButtonClick}
                    onZoomOut={grid.handleZoomOutButtonClick}
                    selectedDiagramName={grid.selectedDiagramName}
                    zoom={grid.zoom}
                />
            )}
        </>
    );
}
